// Global image cache to prevent duplicate requests and downloads
interface ImageCacheEntry {
  url: string;
  status: 'loading' | 'loaded' | 'error';
  promise?: Promise<string>;
  timestamp: number;
}

class GlobalImageCache {
  private cache = new Map<string, ImageCacheEntry>();
  private maxSize = 1000; // Maximum number of cached entries
  private ttl = 5 * 60 * 1000; // 5 minutes TTL

  // Get or create a cached image entry
  getOrCreate(url: string): ImageCacheEntry {
    const now = Date.now();
    
    // Check if we have a valid cached entry
    const existing = this.cache.get(url);
    if (existing && (now - existing.timestamp) < this.ttl) {
      return existing;
    }

    // Remove expired entry if exists
    if (existing) {
      this.cache.delete(url);
    }

    // Create new entry
    const entry: ImageCacheEntry = {
      url,
      status: 'loading',
      timestamp: now,
      promise: this.loadImage(url)
    };

    // Clean up cache if it's too large
    if (this.cache.size >= this.maxSize) {
      this.cleanup();
    }

    this.cache.set(url, entry);
    return entry;
  }

  // Load image and update cache entry
  private async loadImage(url: string): Promise<string> {
    try {
      // For external images, be more conservative with preloading
      if (this.isExternalImage(url)) {
        // For external images, just mark as loading and let the browser handle it
        // This prevents CORS issues while still tracking the loading state
        const entry = this.cache.get(url);
        if (entry) {
          entry.status = 'loading';
        }
        return url;
      }

      // For local images, we can safely preload
      const promise = new Promise<string>((resolve) => {
        const img = new Image();
        
        img.onload = () => {
          const entry = this.cache.get(url);
          if (entry) {
            entry.status = 'loaded';
          }
          resolve(url);
        };
        
        img.onerror = () => {
          const entry = this.cache.get(url);
          if (entry) {
            entry.status = 'error';
          }
          // Don't reject, just mark as error but still resolve
          // This allows the image to be displayed even if preloading fails
          resolve(url);
        };
        
        img.src = url;
      });

      return await promise;
    } catch {
      const entry = this.cache.get(url);
      if (entry) {
        entry.status = 'error';
      }
      // Return the URL anyway, let the browser handle the loading
      return url;
    }
  }

  // Check if image is from external domain
  private isExternalImage(url: string): boolean {
    if (typeof window === 'undefined') return false;
    
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.origin !== window.location.origin;
    } catch {
      return false;
    }
  }

  // Get cached entry without creating new one
  get(url: string): ImageCacheEntry | undefined {
    const entry = this.cache.get(url);
    if (entry && (Date.now() - entry.timestamp) < this.ttl) {
      return entry;
    }
    return undefined;
  }

  // Check if image is already loaded
  isLoaded(url: string): boolean {
    const entry = this.get(url);
    return entry?.status === 'loaded';
  }

  // Check if image is currently loading
  isLoading(url: string): boolean {
    const entry = this.get(url);
    return entry?.status === 'loading';
  }

  // Preload an image
  preload(url: string): Promise<string> {
    const entry = this.getOrCreate(url);
    return entry.promise || Promise.resolve(url);
  }

  // Clean up expired entries
  private cleanup(): void {
    const now = Date.now();
    const expired = Array.from(this.cache.entries())
      .filter(([_, entry]) => (now - entry.timestamp) >= this.ttl)
      .map(([url]) => url);

    expired.forEach(url => this.cache.delete(url));
  }

  // Clear all cache
  clear(): void {
    this.cache.clear();
  }

  // Get cache statistics
  getStats() {
    const now = Date.now();
    const entries = Array.from(this.cache.values());
    
    return {
      total: entries.length,
      loaded: entries.filter(e => e.status === 'loaded').length,
      loading: entries.filter(e => e.status === 'loading').length,
      error: entries.filter(e => e.status === 'error').length,
      expired: entries.filter(e => (now - e.timestamp) >= this.ttl).length
    };
  }
}

// Global instance
export const globalImageCache = new GlobalImageCache();

// Utility functions
export const preloadImage = (url: string): Promise<string> => {
  return globalImageCache.preload(url);
};

export const isImageLoaded = (url: string): boolean => {
  return globalImageCache.isLoaded(url);
};

export const isImageLoading = (url: string): boolean => {
  return globalImageCache.isLoading(url);
};

// Batch preload multiple images
export const preloadImages = async (urls: string[]): Promise<string[]> => {
  const uniqueUrls = [...new Set(urls)]; // Remove duplicates
  
  // Filter out external images to avoid CORS issues
  const localUrls = uniqueUrls.filter(url => {
    if (typeof window === 'undefined') return true;
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.origin === window.location.origin;
    } catch {
      return true;
    }
  });
  
  const promises = localUrls.map(url => preloadImage(url));
  return Promise.allSettled(promises).then(results => 
    results.map(result => 
      result.status === 'fulfilled' ? result.value : null
    ).filter(Boolean) as string[]
  );
}; 